import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { Search, X, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RNWorkOrderRow } from "../hooks/useRNWorkOrderList";

interface Props {
  search: string;
  setSearch: (val: string) => void;
  rows: RNWorkOrderRow[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  absoluteTotal: number;
  setPage: (p: number) => void;
  onSelect?: (row: RNWorkOrderRow) => void;
  currentIngredientId?: string; // Current ingredient ID for list-based nav
  currentIngredientCode?: string; // FIX 1: Explicit code prop — stable, not derived from list
  onModalOpen?: () => void; // Callback when modal opens to trigger initial fetch
}

const RNSearch: React.FC<Props> = ({
  search,
  setSearch,
  rows,
  loading,
  page,
  totalPages,
  total,
  absoluteTotal,
  setPage,
  onSelect,
  currentIngredientId,
  currentIngredientCode,
  onModalOpen,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasTriggeredModalOpen = useRef(false);

  // Type-to-jump: null = not editing (show current record); non-null = user is typing a code.
  const [typed, setTyped] = useState<string | null>(null);
  const pendingCode = useRef<string | null>(null);

  // Code accessor — hoisted so both the Enter-to-jump handler and the results
  // table can use the same logic (was previously defined inline in the .map).
  const getCode = (r: RNWorkOrderRow): string => {
    if (typeof r.items === 'string') return r.items;
    return r.items?.ident || '-';
  };

  // Typed value → rows (task 47): the RN number first (26-6020-00009), then only the
  // sequence (9 / 00009), then the ident — one ident has many RNs, so it is the last resort.
  const findMatches = (code: string): RNWorkOrderRow[] => {
    const needle = code.toLowerCase();
    const byNumber = rows.filter(row => row.number?.toLowerCase() === needle);
    if (byNumber.length) return byNumber;
    if (/^\d+$/.test(needle)) {
      const seq = Number(needle);
      const bySeq = rows.filter(row => Number(row.number?.split('-').pop()) === seq);
      if (bySeq.length) return bySeq;
    }
    return rows.filter(row => getCode(row)?.toLowerCase() === needle);
  };

  // FIX 3: Keep highlightedIndex in a ref so the IntersectionObserver callback
  // can read the current value without being in its dependency array.
  const highlightedIndexRef = useRef(highlightedIndex);
  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex;
  }, [highlightedIndex]);

  // FIX 4: Track the previous search value so we only reset the highlight when
  // the query actually changes, not on every page-1 background refetch.
  const prevSearchRef = useRef(search);

  // Handle highlighted index when rows change
  useEffect(() => {
    if (rows.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    // FIX 4: Only reset to 0 when the search query changed (new result set),
    // not on every page-1 fetch that happens during navigation or background reloads.
    const searchChanged = search !== prevSearchRef.current;
    prevSearchRef.current = search;

    setHighlightedIndex(prev => {
      if (prev === -1 || searchChanged) return 0;
      return prev < rows.length ? prev : rows.length - 1;
    });
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  // NOTE: `search` intentionally omitted — we read prevSearchRef inside the effect.

  // Focus search input when modal opens and trigger initial fetch
  useEffect(() => {
    if (isModalOpen && !hasTriggeredModalOpen.current) {
      hasTriggeredModalOpen.current = true;
      onModalOpen?.();
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    } else if (!isModalOpen) {
      hasTriggeredModalOpen.current = false;
    }
  }, [isModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // FIX 3: Infinite scroll observer — highlightedIndex is NOT in deps; we read
  // it via highlightedIndexRef to avoid tearing down/recreating on every keypress.
  useEffect(() => {
    if (!loadingRef.current || loading || page >= totalPages) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading) {
          const currentHighlighted = highlightedIndexRef.current;
          setPage(page + 1);

          if (currentHighlighted >= 0) {
            setTimeout(() => {
              const element = document.querySelector(`[data-row-index="${currentHighlighted}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'auto', block: 'nearest' });
              }
            }, 100);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        root: scrollContainerRef.current
      }
    );

    observer.observe(loadingRef.current);
    return () => observer.disconnect();
  }, [loading, page, totalPages, setPage]);
  // NOTE: highlightedIndex deliberately excluded from deps (FIX 3).

  const openModalAndSync = (value: string) => {
    setSearch(value);
    setIsModalOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const [isAtFirstIngredient, setIsAtFirstIngredient] = useState(false);
  const [isAtLastIngredient, setIsAtLastIngredient] = useState(false);

  // FIX 2: Boundary detection — when the current ingredient is not in the visible
  // (possibly filtered/paged) list, findIndex returns -1.  Treat -1 as "unknown
  // position" → mark both boundary flags as false (buttons stay enabled, no wrong
  // jump).  Only mark a boundary when we have positive confirmation of position.
  useEffect(() => {
    if (!currentIngredientId || rows.length === 0) {
      setIsAtFirstIngredient(false);
      setIsAtLastIngredient(false);
      return;
    }

    const currentIndex = rows.findIndex(row => row._id === currentIngredientId);

    if (currentIndex === -1) {
      // Cannot determine position in filtered/paged list — disable neither button.
      setIsAtFirstIngredient(false);
      setIsAtLastIngredient(false);
      return;
    }

    // List is sorted newest first (createdAt: -1), so:
    // - Index 0 on page 1      = newest → isAtLastIngredient
    // - Last index on last page = oldest → isAtFirstIngredient
    setIsAtFirstIngredient(currentIndex === rows.length - 1 && page === totalPages);
    setIsAtLastIngredient(currentIndex === 0 && page === 1);
  }, [currentIngredientId, rows, page, totalPages]);

  // FIX 2: Navigation handlers — guard against findIndex returning -1 so we
  // never make a wrong jump.  When position is unknown (filtered list), do nothing
  // rather than jumping to an incorrect record.
  const handleFirstIngredient = () => {
    // First = oldest work order (last index on last page)
    if (page !== totalPages) {
      setPage(totalPages);
    } else if (rows.length > 0) {
      onSelect?.(rows[rows.length - 1]);
    }
  };

  const handlePreviousIngredient = () => {
    if (!currentIngredientId) return;

    const currentIndex = rows.findIndex(row => row._id === currentIngredientId);

    if (currentIndex === -1) {
      // Position unknown (modal is filtered) — safe no-op to prevent wrong jump.
      return;
    }

    if (currentIndex < rows.length - 1) {
      // Older work order in current page (higher index — list is newest-first)
      onSelect?.(rows[currentIndex + 1]);
    } else if (currentIndex === rows.length - 1 && page < totalPages) {
      // At end of page — load next page (which has older records)
      setPage(page + 1);
    }
    // else: already at oldest on last page — isAtFirstIngredient is true, button disabled
  };

  const handleNextIngredient = () => {
    if (!currentIngredientId) return;

    const currentIndex = rows.findIndex(row => row._id === currentIngredientId);

    if (currentIndex === -1) {
      // Position unknown (modal is filtered) — safe no-op to prevent wrong jump.
      return;
    }

    if (currentIndex > 0) {
      // Newer work order in current page (lower index — list is newest-first)
      onSelect?.(rows[currentIndex - 1]);
    } else if (currentIndex === 0 && page > 1) {
      // At start of page — load previous page (which has newer records)
      setPage(page - 1);
    }
    // else: already at newest on first page — isAtLastIngredient is true, button disabled
  };

  const handleLastIngredient = () => {
    // Last = newest work order (first index on first page)
    if (page !== 1) {
      setPage(1);
    } else if (rows.length > 0) {
      onSelect?.(rows[0]);
    }
  };

  const hasNoIngredients = !currentIngredientId;

  const handleKeyDownOnInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const newIndex = prev < rows.length - 1 ? prev + 1 : prev;
        setTimeout(() => {
          const element = document.querySelector(`[data-row-index="${newIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);
        return newIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : prev;
        setTimeout(() => {
          const element = document.querySelector(`[data-row-index="${newIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);
        return newIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && rows[highlightedIndex]) {
        onSelect?.(rows[highlightedIndex]);
        setIsModalOpen(false);
        setSearch("");
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsModalOpen(false);
      setSearch("");
    }
  };

  // FIX 1: Use the explicit currentIngredientCode prop as PRIMARY source for
  // the toolbar display.  Fall back to a list lookup only when the prop is empty
  // (e.g. nothing is loaded yet).  This keeps the toolbar stable regardless of
  // what is in the modal's filtered search list.
  const getToolbarDisplay = (): string => {
    if (!currentIngredientId) return "";

    // Primary: explicit prop threaded from the loaded ingredient
    if (currentIngredientCode) return currentIngredientCode;

    // Fallback: derive from list (works when current item happens to be in-page)
    const found = rows.find(row => row._id === currentIngredientId);
    if (!found) return "";
    if (typeof found.items === 'string') return found.items;
    return found.items?.ident || found.items?.name || '-';
  };

  // Resolve a typed code once the parent's list (re-filtered via setSearch)
  // comes back. 0 matches or >1 -> escalate to the modal, already filtered.
  useEffect(() => {
    const code = pendingCode.current;
    if (!code || loading) return;
    pendingCode.current = null;
    const matches = findMatches(code);
    if (matches.length === 1) {
      onSelect?.(matches[0]);
      setTyped(null);
      setSearch("");
      return;
    }
    setIsModalOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, [rows, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative inline-flex items-center gap-0">
      {/* First ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handleFirstIngredient}
        disabled={hasNoIngredients || isAtFirstIngredient}
        aria-label="First ingredient"
        title="First ingredient"
      >
        <ChevronsLeft className="w-4 h-4" />
      </button>

      {/* Previous ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handlePreviousIngredient}
        disabled={hasNoIngredients || isAtFirstIngredient}
        aria-label="Previous ingredient"
        title="Previous ingredient"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Search input with integrated search icon */}
      <div className="group relative" style={{ width: '18ch' }}>
        <input
          type="text"
          placeholder={t("search.searchNormativ") || "Пребарувај Норматив"}
          className="w-full pl-3 pr-9 py-2 h-9 border border-gray-300 bg-white shadow-sm focus:bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-center font-semibold text-sm text-gray-800 cursor-text select-all"
          value={typed ?? getToolbarDisplay()}
          onChange={(e) => setTyped(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => {
            if (typed !== null && pendingCode.current === null) setTyped(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "F4") {
              e.preventDefault();
              if (typed && typed.trim()) {
                setSearch(typed.trim());
              }
              setIsModalOpen(true);
              setTimeout(() => searchInputRef.current?.focus(), 100);
              return;
            }
            if (e.key === "Escape") {
              setTyped(null);
              e.currentTarget.blur();
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              const code = typed?.trim();
              if (!code) return;
              const matches = findMatches(code);
              if (matches.length === 1) {
                onSelect?.(matches[0]);
                setTyped(null);
                setSearch("");
                return;
              }
              pendingCode.current = code;
              setSearch(code);
              return;
            }
          }}
        />
        {/* Search icon - clickable to open modal */}
        <button
          type="button"
          onClick={() => {
            setIsModalOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 100);
          }}
          className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 hover:text-gray-600 transition-colors"
          aria-label="Search"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Next ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handleNextIngredient}
        disabled={hasNoIngredients || isAtLastIngredient}
        aria-label="Next ingredient"
        title="Next ingredient"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Last ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handleLastIngredient}
        disabled={hasNoIngredients || isAtLastIngredient}
        aria-label="Last ingredient"
        title="Last ingredient"
      >
        <ChevronsRight className="w-4 h-4" />
      </button>

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={() => {}} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[78vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex flex-col">
                <Dialog.Title className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-600" />
                  {t("search.searchNormativ") || "Search Normativ"}
                </Dialog.Title>
                <p className="text-xs text-gray-600 mt-1">
                  {t("search.keyboardInstructions") || "Use arrow keys to navigate, Enter to select"}
                </p>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSearch("");
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 flex flex-col overflow-hidden">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDownOnInput}
                  placeholder={t("search.searchNormativPlaceholder") || "Type to search normativ..."}
                  className="w-full pl-10 pr-4 h-10 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                  autoFocus
                />
              </div>

              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto border border-gray-200 rounded-lg"
                style={{ minHeight: '220px', maxHeight: '52vh', scrollBehavior: 'auto' }}
              >
                {loading && rows.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Searching...</span>
                  </div>
                ) : (
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left">
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.workOrderNumber", "Бр. на работен налог")}</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.idents") || "Code"}</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.name") || "Name"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => {
                        // Helper to get name
                        const getName = (r: typeof row) => {
                          if (typeof r.items === 'object' && r.items) {
                            return r.items.name || '-';
                          }
                          return '-';
                        };
                        return (
                          <tr
                            key={row._id}
                            data-row-index={idx}
                            className={`border-b border-gray-100 cursor-pointer transition-all duration-200 ${
                              idx === highlightedIndex
                                ? "bg-blue-50 border-l-4 border-blue-500 ring-2 ring-blue-500/50 shadow-md"
                                : "hover:bg-gray-50"
                            }`}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            onClick={() => {
                              onSelect?.(row);
                              setIsModalOpen(false);
                              setSearch("");
                            }}
                          >
                            <td className={`py-3 px-4 font-medium transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-900" : "text-gray-900"
                            }`}>{row.number || "-"}</td>
                            <td className={`py-3 px-4 font-medium transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-900" : "text-gray-900"
                            }`}>{getCode(row)}</td>
                            <td className={`py-3 px-4 transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-800" : "text-gray-700"
                            }`}>{getName(row)}</td>
                          </tr>
                        );
                      })}
                      {rows.length === 0 && !loading && (
                        <tr>
                          <td colSpan={3} className="text-center text-gray-500 py-12">
                            <div className="flex flex-col items-center gap-2">
                              <Search className="w-8 h-8 text-gray-300" />
                              <p>{t("search.noResults") || "No work orders found"}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}

                {/* Infinite scroll loading indicator */}
                {loading && page > 1 && (
                  <div className="flex items-center justify-center py-3 border-t border-gray-100">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-xs text-gray-600">Loading more...</span>
                  </div>
                )}

                {/* Invisible trigger for infinite scroll */}
                {page < totalPages && (
                  <div
                    ref={loadingRef}
                    className="h-1 w-full"
                    style={{ opacity: 0 }}
                  />
                )}
              </div>

              <div className="flex items-center justify-center mt-4 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-600">
                  {rows.length > 0 && (
                    <span>
                      {t("search.showingResults", { current: rows.length, total: absoluteTotal }) ||
                       `Showing ${rows.length} of ${absoluteTotal} results`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default RNSearch;
